/* eslint-disable no-restricted-globals */
import { createApp, createInitialState } from '@/app'
import { createApp as vueCreateApp, createSSRApp } from 'vue'
import { createHead } from '@unhead/vue/client'

import App from '@/App.vue'
import { createWebHistory } from 'vue-router'
import {
    createClientRedirect,
    createSetStatusState,
    provideSetStatus,
} from 'wucdbm-vite-vue-ssr'
import { logErrorEvent } from '@/helpers/error'
import type { PublicConfig } from '@/entry-server-types'
import { setCommissionJunctionEvent } from '@/helpers/commission-junction'
import Cookies from 'js-cookie'
import { uncrunch } from 'graphql-crunch'
import type { InitialStateWithObjectSSRData } from '@/initial-state.ts'
import { logUnexpectedError } from '@/helpers'

window.addEventListener('error', (e: ErrorEvent) => {
    logErrorEvent(e)
})

let state = createInitialState()

if (window.__INITIAL_STATE__) {
    const initialStateWithObjectSSRData = uncrunch(
        window.__INITIAL_STATE__
    ) as unknown as InitialStateWithObjectSSRData

    state = {
        ...initialStateWithObjectSSRData,
        api: Object.fromEntries(
            Object.entries(initialStateWithObjectSSRData.api).map(
                ([key, value]) => {
                    if (undefined === value.data) {
                        return [key, value]
                    }

                    const newValue = {
                        ...value,
                        data: JSON.stringify(value.data),
                    }

                    return [key, newValue]
                }
            )
        ),
        rb: initialStateWithObjectSSRData.rb,
    }
}

function getAppConfig(): PublicConfig {
    if (window.__APP_CONFIG__) {
        return window.__APP_CONFIG__
    }

    return {
        urls: {
            api: API_URL,
        },
        analyticsId: SEGMENT_WRITE_KEY,
        googleSignInId: GOOGLE_SIGN_IN_ID,
        facebookAppId: FACEBOOK_APP_ID,
        msClarityId: MS_CLARITY_ID,
        checkoutComApiUrl: CHECKOUT_COM_API,
        sentry: SENTRY_DSN
            ? {
                  dsn: SENTRY_DSN,
                  environment: SENTRY_ENVIRONMENT,
              }
            : undefined,
        intercom: {
            app_id: INTERCOM_APP_ID,
        },
        affirmUS: {
            publicKey: AFFIRM_PUBLIC_KEY_US,
            scriptUrl: AFFIRM_SCRIPT_URL_US,
        },
        affirmGB: {
            publicKey: AFFIRM_PUBLIC_KEY_GB,
            scriptUrl: AFFIRM_SCRIPT_URL_GB,
        },
        meta: undefined,
        fouanalytics: {
            enabled: JSON.parse(FOUANALYTICS_ENABLED || 'false'),
        },
    }
}

const {
    urls,
    analyticsId,
    googleSignInId,
    facebookAppId,
    msClarityId,
    checkoutComApiUrl,
    sentry,
    intercom,
    affirmUS,
    affirmGB,
    meta,
    fouanalytics,
} = getAppConfig()

const container = document.getElementById('app')
const isSSR =
    container && container.getAttribute('data-server-rendered') === 'true'

const redirect = createClientRedirect(async (location, status) => {
    const redirect = status === 301 ? router.replace : router.push

    await redirect(location)
        // eslint-disable-next-line no-console
        .catch(console.warn)
})

const hasHash = !!window.location.hash
const {
    app,
    router,
    // head,
    // headObject,
    // headUpdatesAllowed,
    robots,
} = createApp(isSSR && !hasHash ? createSSRApp(App) : vueCreateApp(App), {
    history: createWebHistory(import.meta.env.BASE_URL),
    initialState: state,
    urls,
    analyticsId,
    googleSignInId,
    facebookAppId,
    msClarityId,
    checkoutComApiUrl,
    sentry: sentry,
    intercom: intercom,
    affirmUS,
    affirmGB,
    qaIsNoJs: false,
    initialUrl: new URL(window.location.href),
    userAgent: navigator.userAgent,
    cookieProvider: {
        set(name, value, options) {
            const attributes: Cookies.CookieAttributes = {
                expires: options?.expires,
                path: options?.path || '/',
                secure: options?.secure,
                sameSite: options?.sameSite,
            }

            if (options?.httpOnly) {
                logUnexpectedError(
                    'Not expecting to have httpOnly cookies on client',
                    {
                        name,
                    }
                )
            } else {
                if (value === undefined) {
                    Cookies.remove(name, attributes)
                } else {
                    Cookies.set(name, value, attributes)
                }
            }
        },
        get(name) {
            return Cookies.get(name)
        },
    },
    meta,
    fetch,
    exchanges: [],
    fouanalytics: fouanalytics,
    createHead,
    redirect,
})

const statusState = createSetStatusState()
provideSetStatus(app, statusState)

// watch(
//     headObject,
//     () => {
//         if (headUpdatesAllowed.value) {
//             head.updateDOM(document)
//         }
//     },
//     { deep: true }
// )

router.beforeEach(() => {
    robots.reset()
})

router
    .isReady()
    .then(() => {
        // save Commission Junction referral
        setCommissionJunctionEvent(router)
    })
    .then(() => {
        app.mount('#app', true)
    })

if (import.meta.env.PROD) {
    // ЗАКОВАХ ГО
    if (
        typeof navigator !== 'undefined' &&
        typeof navigator.serviceWorker !== 'undefined'
    ) {
        // ДАЖЕ ТРИ ПЪТИ
        navigator.serviceWorker
            .getRegistrations()
            .then(function (registrations) {
                for (const registration of registrations) {
                    registration.unregister().catch((e) => {
                        // eslint-disable-next-line no-console
                        console.error('Failed to unregister ServiceWorker', e)
                    })
                }
            })
    }
    // registerWorker(
    //     `${import.meta.env.BASE_URL}sw.js`,
    //     {},
    //     {
    //         ask: () => {
    //             const conf = confirm('A new version is available. Update?')
    //
    //             if (conf) {
    //                 updateApp()
    //             }
    //         },
    //         refreshHandler: () => {
    //             // eslint-disable-next-line no-console
    //             console.warn('Reloading, please wait')
    //
    //             window.location.reload()
    //         },
    //         updateInterval: 3600,
    //     }
    // )
}
